import React from 'react';
import type { ReactNode } from 'react';
import type { ElementNodeDto } from '@xshop/shared';
import type { ElementMarkAttrs } from '../../editor/editorElement';
import { useElementBox } from '../render/elementRenderContext';
import type { BoundField } from './useFormField';
import './forms.css';

/**
 * The one shape a field is drawn in: a caption, the control, and the message the control earned.
 *
 * Every field element and both of the platform's own forms draw through this, so a merchant never
 * meets two different-looking versions of "this answer is required". The caption is a real `label`
 * pointing at the control's own id, and the message carries the id the control names in
 * `aria-describedby` — a red line a screen reader never reads out is not an error message.
 */

/** The class the style engine wrote for a node, split from the marks the builder selects it by. */
export type FieldBox = { className?: string; marks: Partial<ElementMarkAttrs> };

/** What every control wears so its label, its message and itself are announced as one thing. */
export type ControlAttrs = {
  id: string;
  name: string;
  'aria-required'?: true;
  'aria-invalid'?: true;
  'aria-describedby'?: string;
  /** Only ever set for a control the merchant gave no caption; a visible label is always the better name. */
  'aria-label'?: string;
};

/** Everything the frame draws around one control. */
export type FieldFrameProps = {
  id: string;
  label: string;
  required: boolean;
  error: string;
  errorId: string;
  /** A quiet line under the control — how many photos are allowed, what a file may weigh. */
  hint?: string;
  marks?: Partial<ElementMarkAttrs>;
  children: ReactNode;
};

/**
 * Splits what the style engine wrote for a field node. The class belongs on the control, because the
 * catalog's own defaults for a field are its border, its padding and its focus ring; the builder's mark
 * belongs on the wrapper, so clicking a field's caption selects the field rather than nothing at all.
 */
export function useFieldBox(node: ElementNodeDto): FieldBox {
  const { className, ...marks } = useElementBox(node);
  return { className, marks };
}

// Names the attributes one control wears, pointing at its own message only while that message is showing.
export const controlAttrs = (field: BoundField, unnamed?: string): ControlAttrs => ({
  id: field.rule.domId,
  name: field.rule.name,
  'aria-required': field.rule.required ? true : undefined,
  'aria-invalid': field.error ? true : undefined,
  'aria-describedby': field.error ? field.errorId : undefined,
  'aria-label': field.rule.label === '' ? unnamed || field.rule.name : undefined
});

// Draws the caption over a control, the control, and the message it earned.
export const FieldFrame: React.FC<FieldFrameProps> = ({ id, label, required, error, errorId, hint, marks, children }) => (
  <div className="xs-form-field" {...marks}>
    {label === '' ? null : (
      <label className="xs-form-caption" htmlFor={id} dir="auto">
        {label}{required ? ' *' : ''}
      </label>
    )}
    {children}
    {hint === undefined || hint === '' ? null : <span className="xs-form-hint" dir="auto">{hint}</span>}
    {error === '' ? null : <span className="xs-form-error" id={errorId} dir="auto">{error}</span>}
  </div>
);
